import LinkButton from '../LinkButton'
import { Button, Icon } from '@bm-js/atlascloud-ui'
import {
  useConfirmation,
  useEditorActions,
  useEditorData,
  useEditorStatus,
  useEditorUi,
  useToast,
} from '../../utils/hooks'
import { useMemo } from 'react'
import { FullScreenMode } from '../../contexts/Editor.context'
import { useNavigate } from 'react-router'
import { ROUTE } from '../../cnst/router.cnst'
import { EDITOR_TOP_BAR_HEIGHT } from '../../cnst/editor.cnst'
import { removePageFromConfig } from '../../utils/editor.utils'
import { TrKey } from '../../cnst/translations/trKey.cnst'
import { useTr } from '../../utils/useTr'
import { useForm } from '../../hooks/useForm'
import PageForm from '../PageForm'
import { LucideIconName } from '@bm-js/atlascloud-shared'

const previewButtons: {
  mode: FullScreenMode
  icon: LucideIconName
  text: TrKey
}[] = [
  {
    mode: FullScreenMode.mobile,
    icon: LucideIconName.Smartphone,
    text: TrKey.editorTopPreviewMobile,
  },
  {
    mode: FullScreenMode.desktop,
    icon: LucideIconName.Laptop,
    text: TrKey.editorTopPreviewDesktop,
  },
]

const EditorBarTop = () => {
  const { tr } = useTr()
  const confirmation = useConfirmation()
  const toast = useToast()
  const navigate = useNavigate()
  const {
    setActivePage,
    saveConfig,
    publishConfig,
    setUi,
    patchConfig,
    patchActivePage,
  } = useEditorActions()

  const { isSaved, isPublished, isSaving, isPublishing } = useEditorStatus()
  const { fullScreen } = useEditorUi()
  const { breadcrumbs, workingConfig, activePage } = useEditorData()
  const { features } = useEditorData().backendResponse.website

  const editPageForm = useForm({
    formComponent: PageForm,
    extras: { features },
  })

  const leaveEditor = async () => {
    let proceed = isSaved
    if (!proceed) {
      proceed = await confirmation({
        message: tr(TrKey.editorUnsavedChangesMessage),
        title: tr(TrKey.editorUnsavedChangesTitle),
        proceedText: tr(TrKey.editorUnsavedChangesProceed),
        abortText: tr(TrKey.editorUnsavedChangesAbort),
      })
    }
    if (!proceed) return
    navigate(ROUTE.DASHBOARD)
  }

  const editPage = async () => {
    const result = await editPageForm({
      initialValue: activePage || undefined,
    })
    if (result.status !== 'success') return
    patchActivePage(result.data)
    setActivePage(result.data.uuid)
  }

  const removePage = async () => {
    if (!activePage) return
    const proceed = await confirmation({
      message: tr(TrKey.editorRemovePageConfirmationText),
      title: tr(TrKey.editorRemovePageConfirmationTitle),
      proceedText: tr(TrKey.remove),
      abortText: tr(TrKey.cancel),
    })
    if (!proceed) return
    const { pages } = removePageFromConfig(workingConfig, activePage.uuid)
    patchConfig({ pages })
    setActivePage(pages[0].uuid)
  }

  const setFullScreenMode = (fullScreen: FullScreenMode) => {
    setUi((ui) => ({ ...ui, fullScreen }))
    toast.info(tr(TrKey.editorPressEscapeToLeaveFullscreen))
  }

  const operationInProgress = useMemo(() => {
    return isSaving || isPublishing
  }, [isSaving, isPublishing])

  const currentPageIndicatorText = useMemo(() => {
    if (!breadcrumbs.length) return tr(TrKey.editorNoPageSelected)
    const trace = breadcrumbs.map((c) => c.viewName).join(' / ')
    return tr(TrKey.editorEditingPage, { pageName: trace })
  }, [breadcrumbs, tr])

  const containerStyles = useMemo(() => {
    if (fullScreen) {
      return {
        height: 0,
        padding: 0,
      }
    }

    return {
      height: EDITOR_TOP_BAR_HEIGHT,
    }
  }, [fullScreen])

  return (
    <div
      className='grid grid-cols-12 bg-white items-center px-4 border-b border-slate-500 transition-all overflow-hidden'
      style={containerStyles}
    >
      <div className='col-span-3'>
        <LinkButton
          icon={LucideIconName.SquareArrowLeft}
          reverse
          onClick={leaveEditor}
        >
          {tr(TrKey.editorTopBack)}
        </LinkButton>
      </div>
      <div className='col-span-6 grid items-center gap-4 justify-self-center grid-cols-12'>
        <p className='truncate col-span-8' title={currentPageIndicatorText}>
          {currentPageIndicatorText}
        </p>
        <div className='flex gap-2 whitespace-nowrap col-span-4'>
          <Button sm onClick={editPage}>
            {tr(TrKey.edit)}
          </Button>
          <Button sm danger onClick={removePage}>
            {tr(TrKey.remove)}
          </Button>
        </div>
      </div>
      <div className='col-span-1 flex gap-2'>
        {previewButtons.map((button) => (
          <button
            key={button.text}
            title={tr(button.text)}
            onClick={() => setFullScreenMode(button.mode)}
          >
            <Icon name={button.icon} className='size-5' />
          </button>
        ))}
      </div>
      <div className='col-span-2 flex gap-2 justify-self-end'>
        <Button disabled={operationInProgress || isSaved} onClick={saveConfig}>
          {isSaved
            ? tr(TrKey.editorTopSaved)
            : isSaving
              ? tr(TrKey.saving)
              : tr(TrKey.save)}
        </Button>
        <Button
          disabled={operationInProgress || isPublished}
          onClick={publishConfig}
        >
          {isPublished
            ? tr(TrKey.editorTopPublished)
            : isPublishing
              ? tr(TrKey.editorTopPublishing)
              : tr(TrKey.editorTopPublish)}
        </Button>
      </div>
    </div>
  )
}

export default EditorBarTop
